import React, { Suspense, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { getRange } from '@inter-ikea-storageone/shared/utils/queryUtils';
import { getRangeName } from '@inter-ikea-storageone/shared/utils/rangeName';
import { AppRoutes } from '@app/routes';
import { useLandingPageConfig } from '@app/hooks/useLandingPageConfig';
import { isKiosk } from '@inter-ikea-storageone/shared/utils/platform';
import { useIsMobile } from '@app/contexts/viewport';
import { KompisContentMargins } from '@inter-ikea-kompis/react-components';
import { ContentMarginsLayoutEnum } from '@inter-ikea-kompis/component-content-margins';
import { isUpptackaActive } from '@inter-ikea-storageone/shared/utils/integration';
import RtlStyles from '@app/i18n/rtl';
import useSetHtmlLanguageAttributes from '@app/utils/htmlLanguageAttributes';
import { IntlProvider, useIntl } from 'react-intl';
import LandingPageHeader from '@app/views/LandingPage/ui/LandingPageHeader/LandingPageHeader';
import LandingPageSheetsWithTransition from '@app/views/LandingPage/ui/LandingPageSheetsWithTransition';
import { setLoadVpcSheetVisible, showOpenDesignSheet } from '@app/stores/designStore';
import { useDexfSettings } from '@app/views/LandingPage/hooks/useDexfSettings';
import { useResourcePrefetching } from '@app/views/LandingPage/hooks/useResourcePrefetching';
import { useTranslations } from '@app/views/LandingPage/hooks/useTranslations';
import { useLandingPageCardContent } from '@app/views/LandingPage/hooks/useLandingPageCardContent';
import Box from '@app/ui/Box';
import {
  reportBookRemotePlanningLightweight,
  reportToggleModalLightweight
} from '@app/analytics/lightweightReporters';
import { AnalyticsViewSource } from '@inter-ikea-storageone/shared/analytics';
import {
  stripURLOfQueryParameters,
  getQuery
} from '@inter-ikea-storageone/shared/utils/queryUtils';
import {
  Container,
  GridContainer,
  Grid,
  RangeTitleBackgroundContainer,
  TitleWrapperMobile,
  LandingPageTitle,
  TitleWrapper,
  SimpleGrid,
  StyledCard,
  CarouselContainer
} from './LandingPageStyles';
// Import ingka/skapa CSS styles
import '@app/ingkaStyles';
import LandingPageGalleryWithTransition from './LandingPageGalleryWithTransition';

// Lazy load components
const LandingPageBanner = React.lazy(() => import('@app/views/LandingPage/ui/LandingPageBanner'));

/**
 * Landing page with modular hook-based architecture.
 *
 * Flow:
 * 1. Load translations and DEXF settings
 * 2. Prefetch resources (catalogs, components) in background
 * 3. Display cards with content based on configuration
 * 4. Handle navigation to planner or opening design sheets
 *
 * Optimized for fast initial load:
 * - Minimal CSS imports only
 * - Lazy loading heavy components
 * - Deferred resource prefetching
 */
const LandingPageContent: React.FC = () => {
  const isMobile = useIsMobile();
  useSetHtmlLanguageAttributes();
  const range = getRange();
  const rangeName = getRangeName(range);
  const config = useLandingPageConfig();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  // Hook 1: DEXF settings (async) - must be called before any conditional returns
  const {
    dexfSettings,
    enableSaveToProfile,
    experienceGalleryBackURL,
    bookAppointmentEnabled,
    supportBookingLink
  } = useDexfSettings();

  // Inline: Persisted scene check (simple - no need for separate hook)
  const hasPersistedScene = useMemo(() => {
    const storageKey = `storageone-scene-persist-${range}`;
    return !!localStorage.getItem(storageKey);
  }, [range]);

  // Hook 2: Resource prefetching (async)
  useResourcePrefetching(range);

  // Hook 3: Card content (removes nested conditionals)
  const { pageTitleText, designFromScratchCard, openDesignCard } = useLandingPageCardContent({
    dexfSettings,
    range,
    rangeName,
    hasPersistedScene,
    enableSaveToProfile
  });

  // Hook 4: Handle OAuth redirect - auto-open sheets when returning from login
  // Silent auth happens inside lazy-loaded sheets to avoid heavy imports
  useEffect(() => {
    if (!enableSaveToProfile) return;

    const queryParams = new URLSearchParams(window.location.search);
    const state = getQuery().get('state');

    // Handle OAuth error immediately
    if (queryParams.has('error')) {
      const strippedURL = stripURLOfQueryParameters(['error', 'state', 'code']);
      window.history.replaceState({}, '', strippedURL);
      return;
    }

    // Auto-open sheet if returning from OAuth redirect
    // Don't clean URL yet - let sheets handle after they load and perform silent auth
    if (state === 'openDesign' || state === 'saveDesign') {
      showOpenDesignSheet({
        currentPosition: AnalyticsViewSource.landingPage,
        showSavedDesignsTab: state === 'openDesign'
      });
    }
  }, [enableSaveToProfile]);

  // Return null while config is loading (after all hooks are called)
  if (!config) {
    return null;
  }

  // Inline: Navigation handlers (simple - no need for separate hook)
  const handleStartClick = () => {
    navigate(AppRoutes.PLANNER);
  };

  const handleOpenDesignClick = () => {
    reportToggleModalLightweight({
      modal: enableSaveToProfile ? 'OpenDesignSheet' : 'LoadVpcSheet',
      visible: true,
      page: AnalyticsViewSource.landingPage
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.error(error);
    });

    if (enableSaveToProfile) {
      showOpenDesignSheet({
        currentPosition: AnalyticsViewSource.landingPage,
        showSavedDesignsTab: true
      });
    } else {
      setLoadVpcSheetVisible(true);
    }
  };

  // Booking appointment logic
  const bookingPlannerUrl = supportBookingLink;

  const handleBookAppointment = () => {
    reportBookRemotePlanningLightweight({
      currentMenu: AnalyticsViewSource.landingPage
    });
    window.open(bookingPlannerUrl, '_blank');
  };

  return (
    <RtlStyles>
      <Container data-testid="landing-page">
        <LandingPageHeader
          enableSaveToProfile={enableSaveToProfile}
          experienceGalleryBackURL={experienceGalleryBackURL}
          onOpenDesignClick={handleOpenDesignClick}
        />

        <KompisContentMargins
          layout={
            isUpptackaActive()
              ? ContentMarginsLayoutEnum.upptacka
              : ContentMarginsLayoutEnum.default
          }
        >
          <Suspense fallback={null}>
            <LandingPageBanner />
          </Suspense>

          <GridContainer>
            <Grid>
              <RangeTitleBackgroundContainer
                style={{
                  backgroundColor:
                    !isMobile && config.rangeDesign.backgroundColor
                      ? config.rangeDesign.backgroundColor
                      : ''
                }}
              >
                {!isMobile && config.rangeDesign.backgroundImgUrl && (
                  <img src={config.rangeDesign.backgroundImgUrl} alt="" />
                )}
                {isMobile ? (
                  <TitleWrapperMobile>
                    <LandingPageTitle style={{ fontSize: '2.25rem' }}>
                      {pageTitleText}
                    </LandingPageTitle>
                  </TitleWrapperMobile>
                ) : (
                  <TitleWrapper>
                    <LandingPageTitle
                      color={config.rangeDesign.titleTextColor}
                      style={{
                        fontSize: 'clamp(2.55rem, 5vw, 5rem)',
                        wordBreak: 'keep-all',
                        lineHeight: 1.3
                      }}
                    >
                      {pageTitleText}
                    </LandingPageTitle>
                  </TitleWrapper>
                )}
              </RangeTitleBackgroundContainer>

              <SimpleGrid>
                <StyledCard
                  title={designFromScratchCard.title}
                  body={designFromScratchCard.body}
                  data-testid="item-design-from-scratch"
                  emphasised
                  backgroundColor={config.designFromScratch.backgroundColor}
                  textColor={config.designFromScratch?.textColor || '#000'}
                  imageProps={{
                    src: config.designFromScratch.backgroundImgUrl,
                    alt: ''
                  }}
                  onClick={handleStartClick}
                  ctaLabel={formatMessage({
                    id: 'storageone.landingPageContinueDesignOrStartFromScratchTitle'
                  })}
                  buttonVariant="icon-primary-inverse"
                  imagePosition="leading"
                  imageRatio={!isMobile ? 'square' : 'wide'}
                  compact={!isMobile}
                />

                <StyledCard
                  title={openDesignCard.title}
                  body={openDesignCard.body}
                  imageProps={{
                    src: config.openDesign.backgroundImgUrl,
                    alt: ''
                  }}
                  data-testid="item-open-design"
                  emphasised
                  backgroundColor={config.openDesign.backgroundColor}
                  ctaLabel={formatMessage({
                    id: 'storageone.landingPageBtnOpenDesign'
                  })}
                  textColor={config.openDesign.textColor}
                  buttonVariant="icon-primary-inverse"
                  onClick={handleOpenDesignClick}
                  compact={!isMobile}
                  imagePosition="leading"
                  imageRatio="square"
                />
              </SimpleGrid>
            </Grid>

            <CarouselContainer>
              <LandingPageGalleryWithTransition />
              {bookAppointmentEnabled && !isKiosk() && (
                <Box mt="l">
                  <StyledCard
                    title={formatMessage({
                      id: 'storageone.bookAPlanningSession'
                    })}
                    body={formatMessage({
                      id: 'storageone.bookAPlanningSessionLandingPageDescription'
                    })}
                    data-testid="card-book-planning-session"
                    emphasised
                    backgroundColor="#F5F5F5"
                    ctaLabel={formatMessage({
                      id: 'storageone.bookAPlanningSession'
                    })}
                    buttonVariant="icon-primary-inverse"
                    onClick={handleBookAppointment}
                    compact={!isMobile}
                  />
                </Box>
              )}
            </CarouselContainer>
          </GridContainer>
        </KompisContentMargins>
      </Container>

      <LandingPageSheetsWithTransition enableSaveToProfile={enableSaveToProfile} />
    </RtlStyles>
  );
};

/**
 * Root wrapper with IntlProvider and translation loading.
 */
const LandingPage: React.FC = () => {
  const { translations, locale } = useTranslations();

  if (translations === null) {
    return null;
  }

  return (
    <IntlProvider locale={locale} messages={translations}>
      <LandingPageContent />
    </IntlProvider>
  );
};

export default LandingPage;
